import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import CustomModal from '../common/CustomModal';
import NetGrossField from '../../components/pricing/NetGrossField';
import PriceEntryNote from '../../components/pricing/PriceEntryNote';
import { usePriceListTaxRate } from '../../hooks/usePriceEntry';
import type { PriceTaxRate } from '../../utils/priceTaxRate';
import { GroupPricingStructure } from '../../api/group/groupAdminApiSlice';
import { useGetCustomerClassesQuery } from '../../api/organization/organizationApiSlice';
import { useUpdatePriceScalesMutation } from '../../api/priceCategory/priceCategoryApiSlice';
import { decimalToNumber } from '../../api/asset/assetApiTypes';
import { formatStoredTimeOfDay, localTimeOfDayToStored } from '../../utils/dateTimeUtils';

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type WeekdayToken = (typeof WEEKDAYS)[number];

/** One editable tariff row — duration, weekday scope, time window, price. */
interface EditableRow {
    /** Stable key for React while rows are added/removed. */
    key: string;
    durationMinutes: string;
    days: Set<WeekdayToken>;
    /** Local wall-clock 'HH:MM', empty = no bound. */
    startLocal: string;
    endLocal: string;
    price: string;
}

/** Rows the tariff editor does not understand (venue day-type rows, full-day
 *  day-count rows, class rates on scopes this dialog does not show) are
 *  carried through the save VERBATIM — replacing all scales must never
 *  silently drop a row it merely could not render. `customerClassId` is part
 *  of the row's SCOPE: the backend matches on it and archives unmatched rows,
 *  so a class row passed through without its id is not preserved, it is
 *  archived and replaced by a stripped general copy (#1808; the same failure
 *  `toPriceScalePayload`'s header documents for the object page). */
interface PassthroughRow {
    durationInMinutes: number;
    price: number;
    additionalDaysPrice?: number | null;
    days?: string | null;
    isFullDay?: boolean;
    startTime?: string | null;
    endTime?: string | null;
    customerClassId?: string | null;
}

const isTariffShaped = (days: string | null): boolean => {
    if (days == null) return true; // unconstrained tier — editable
    const tokens = days.split(',').map((t) => t.trim().toLowerCase());
    return tokens.length > 0 && tokens.every((t) => (WEEKDAYS as readonly string[]).includes(t));
};

/** ISO-instant or 'HH:MM:SS' stored bound → 'HH:MM:SS' stored time-of-day. */
const storedTimeOfDay = (value: string | null): string | null => {
    if (!value) return null;
    const iso = /T(\d{2}:\d{2}:\d{2})/.exec(value);
    if (iso) return iso[1];
    const plain = /^(\d{2}:\d{2})(:\d{2})?/.exec(value.trim());
    return plain ? `${plain[1]}${plain[2] ?? ':00'}` : null;
};

let rowCounter = 0;
const nextKey = () => `row-${rowCounter += 1}`;

type EditPriceScalesDialogProps = {
    group: GroupPricingStructure;
    /** The category is shared by other groups — an edit changes them all, so
     *  the dialog says so before the operator types a number. */
    sharedWithOthers: boolean;
    /**
     * The use case is declared FREE: durations stay editable, prices are 0,00
     * and not typeable.
     *
     * This is the shape of the free rule, not a weaker version of it. "Free"
     * means every row prices at zero (ADR-0018 rule 4) — it has never meant
     * "the tenant may not decide which durations are bookable". The backend
     * has always agreed: `freeUseCaseBlockingWrite` admits any write whose
     * rows are all zero, with the comment *"Zero rows are what 'free' means in
     * the data — always admissible"* and, one line above, *"the UI lock alone
     * is not a rule"*. The UI was simply blunter than the rule it mirrored,
     * which left a free storage tenant with the single generated 1440-minute
     * anchor and no way to offer 30 minutes, 1 hour or 2 hours (PO, KGSt-Forum
     * 2026-09-11).
     */
    priceLocked: boolean;
    onClose: () => void;
    /** Called after a successful save so the owner refetches the structure. */
    onSaved: () => void;
};

/** What a row costs on a free use case. One place, so the input, the
 *  validation and the payload cannot disagree about it. */
const FREE_PRICE = '0';

/** One price of a row — the general one or a class rate — typed net or gross (#2967). */
const PriceInput = ({ label, gross, onGrossChange, rate, inputClassName }: Readonly<{
    label: string;
    gross: string;
    onGrossChange: (gross: string) => void;
    rate: PriceTaxRate;
    inputClassName: string;
}>) => (
    <NetGrossField gross={gross} onGrossChange={onGrossChange} rate={rate}>
        {(input) => (
            <label className={'block text-xs text-foreground/60'}>
                {input.label(label)}
                <input
                    type={'number'}
                    min={0}
                    step={'0.01'}
                    className={`${inputClassName} mt-1`}
                    value={input.value}
                    onChange={(e) => input.onChange(e.target.value)}
                />
            </label>
        )}
    </NetGrossField>
);

/**
 * The tariff-row editor — phase 3 of the service-configuration plan; the save
 * path ADR-0018 records as missing ("rules 1 and 4 describe a save path that
 * does not exist today"). Weekday scope, time window, duration, price; the
 * completeness feedback comes from the coverage verdict on the Prices card
 * after saving, computed by the same matcher that prices bookings.
 *
 * Times are entered, shown and stored as LOCAL wall-clock (ADR-0019): what the
 * operator types is what the matcher compares against, in the station's zone,
 * so a boundary typed as 13:00 is 13 Uhr in December as well as in July. This
 * moved with the ADR-0019 migration — before it, the editor pre-shifted every
 * entry to UTC to match the rows already in production.
 *
 * Rows the editor cannot render (venue day-type rates, day-count scales) are
 * passed through verbatim — a replace-all save must never drop what it could
 * not display.
 */
const EditPriceScalesDialog = ({
    group, sharedWithOthers, priceLocked, onClose, onSaved,
}: EditPriceScalesDialogProps) => {
    const { t, i18n } = useTranslation();
    // #2967: net entry converts at the one rate of this price list.
    const rate = usePriceListTaxRate(group.priceCategory?.id);
    const [updatePriceScales, { isLoading }] = useUpdatePriceScalesMutation();
    const [error, setError] = useState<string | null>(null);
    // #1808. A legacy tenant's endpoint answers 409, so this stays empty and
    // the class-rate inputs below never render — correct, not merely
    // convenient: customer classes are an operational-schema concept (same
    // stance as the object page's PricingSection).
    const { data: customerClasses = [] } = useGetCustomerClassesQuery();

    const initial = useMemo(() => {
        const editable: EditableRow[] = [];
        const passthrough: PassthroughRow[] = [];
        // rowKey -> classId -> price string, prefilled from stored class rows
        // whose scope matches an editable row. Class rows on scopes this
        // editor does not show (venue day-type, full-day) stay passthrough.
        const classPrices: Record<string, Record<string, string>> = {};

        // Normalized scope of a stored scale / an editable row, class aside —
        // the client-side mirror of the backend's makeKey.
        const storedScope = (scale: (typeof group.priceScales)[number]): string => [
            (scale.days ?? '')
                .split(',')
                .map((d) => d.trim().toLowerCase())
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b))
                .join(','),
            scale.durationInMinutes ?? '',
            formatStoredTimeOfDay(scale.startTime) ?? '',
            formatStoredTimeOfDay(scale.endTime) ?? '',
        ].join('|');

        const editableKeyByScope = new Map<string, string>();

        for (const scale of group.priceScales) {
            const price = decimalToNumber(scale.price) ?? 0;
            const isGeneral = !scale.customerClassId;
            if (isGeneral && scale.durationInMinutes && isTariffShaped(scale.days) && !scale.isFullDay) {
                const key = nextKey();
                editable.push({
                    key,
                    durationMinutes: String(scale.durationInMinutes),
                    days: new Set(
                        (scale.days ?? '')
                            .split(',')
                            .map((d) => d.trim().toLowerCase())
                            .filter((d): d is WeekdayToken =>
                                (WEEKDAYS as readonly string[]).includes(d)),
                    ),
                    startLocal: formatStoredTimeOfDay(scale.startTime) ?? '',
                    endLocal: formatStoredTimeOfDay(scale.endTime) ?? '',
                    price: String(price),
                });
                editableKeyByScope.set(storedScope(scale), key);
            }
        }

        for (const scale of group.priceScales) {
            const price = decimalToNumber(scale.price) ?? 0;
            if (!scale.customerClassId) {
                if (scale.durationInMinutes && isTariffShaped(scale.days) && !scale.isFullDay) {
                    continue; // already an editable row
                }
            } else {
                const baseKey = editableKeyByScope.get(storedScope(scale));
                if (baseKey) {
                    classPrices[baseKey] = {
                        ...classPrices[baseKey],
                        [scale.customerClassId]: String(price),
                    };
                    continue;
                }
                // Class rate on a scope without an editable base row —
                // carried through verbatim below, never dropped.
            }
            passthrough.push({
                durationInMinutes: scale.durationInMinutes ?? 0,
                price,
                additionalDaysPrice: decimalToNumber(scale.additionalDaysPrice),
                days: scale.days,
                isFullDay: scale.isFullDay,
                startTime: storedTimeOfDay(scale.startTime),
                endTime: storedTimeOfDay(scale.endTime),
                customerClassId: scale.customerClassId ?? null,
            });
        }
        return { editable, passthrough, classPrices };
    }, [group.priceScales]);

    const [rows, setRows] = useState<EditableRow[]>(initial.editable);
    const [classPrices, setClassPrices] = useState<Record<string, Record<string, string>>>(
        initial.classPrices,
    );

    // Only non-default classes get their own inputs: the system default IS the
    // general rate (a booking with no class resolves to it), so a separate row
    // for it would create two spellings of one answer. On a FREE use case
    // every row prices at zero, so a class rate has nothing to say — hidden.
    const offerableClasses = priceLocked ? [] : customerClasses.filter(
        (c) => c.archivedAt === null && !c.isSystemDefault,
    );

    const setClassPrice = (rowKey: string, classId: string, value: string) => {
        setClassPrices((current) => ({
            ...current,
            [rowKey]: { ...current[rowKey], [classId]: value },
        }));
    };

    const updateRow = (key: string, change: Partial<EditableRow>) => {
        setRows((current) => current.map((r) => (r.key === key ? { ...r, ...change } : r)));
    };

    const toggleDay = (key: string, day: WeekdayToken) => {
        setRows((current) => current.map((r) => {
            if (r.key !== key) return r;
            const days = new Set(r.days);
            if (days.has(day)) days.delete(day);
            else days.add(day);
            return { ...r, days };
        }));
    };

    const addRow = () => {
        setRows((current) => [...current, {
            key: nextKey(),
            durationMinutes: '60',
            days: new Set(WEEKDAYS),
            startLocal: '',
            endLocal: '',
            // Deliberately empty, never prefilled with 0: under `mixed` a zero
            // must be ENTERED, not defaulted (plan §3.1). On a FREE use case
            // there is nothing to enter — zero is the only admissible value,
            // so prefilling it is stating the rule, not defaulting past it.
            price: priceLocked ? FREE_PRICE : '',
        }]);
    };

    const removeRow = (key: string) => {
        setRows((current) => current.filter((r) => r.key !== key));
        // The class rates hang off the row's scope: removing the row removes
        // them too (the backend archives whatever the payload no longer
        // carries — here that is the intended outcome, not the failure mode).
        setClassPrices((current) => {
            if (!(key in current)) return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    /** Mirror of the backend rules the save would bounce on, so the dialog
     *  explains the problem at the row instead of after the round-trip. */
    const rowError = (row: EditableRow): string | null => {
        const duration = Number(row.durationMinutes);
        if (!Number.isInteger(duration) || duration <= 0) {
            return t('services.editor.error_duration');
        }
        // On a free use case the price is not the operator's to enter, so it is
        // not theirs to get wrong either.
        if (!priceLocked) {
            const price = Number(row.price);
            if (row.price.trim() === '' || !Number.isFinite(price) || price < 0) {
                return t('services.editor.error_price');
            }
        }
        const hasWindow = !!row.startLocal || !!row.endLocal;
        if (hasWindow && row.days.size === 0) {
            // ADR-0018 rule 1: a windowed row needs explicit weekdays.
            return t('services.editor.error_window_needs_days');
        }
        if (row.startLocal && row.endLocal) {
            const start = localTimeOfDayToStored(row.startLocal);
            const end = localTimeOfDayToStored(row.endLocal);
            if (start && end && start >= end) {
                return t('services.editor.error_window_order');
            }
        }
        return null;
    };

    const scopeOf = (row: EditableRow): string =>
        [
            WEEKDAYS.filter((d) => row.days.has(d)).join(','),
            Number(row.durationMinutes),
            row.startLocal,
            row.endLocal,
        ].join('::');

    const duplicateScopes = useMemo(() => {
        const seen = new Set<string>();
        const dupes = new Set<string>();
        rows.forEach((row) => {
            const key = scopeOf(row);
            if (seen.has(key)) dupes.add(key);
            seen.add(key);
        });
        return dupes;
    }, [rows]);

    /** A class price is optional — blank means "the general rate applies" —
     *  but a non-blank value must be a valid price, mirroring rowError. */
    const classPriceError = (raw: string): boolean => {
        if (raw.trim() === '') return false;
        const price = Number(raw);
        return !Number.isFinite(price) || price < 0;
    };

    const anyClassPriceInvalid = rows.some((row) =>
        Object.values(classPrices[row.key] ?? {}).some((raw) => classPriceError(raw)));

    const anyInvalid = rows.some((row) => rowError(row) !== null)
        || duplicateScopes.size > 0
        || anyClassPriceInvalid;

    const handleSave = async () => {
        setError(null);
        const scopeFields = (row: EditableRow) => ({
            durationInMinutes: Number(row.durationMinutes),
            // "All seven days" is stored as all seven, never blank
            // (ADR-0018 rule 1); no days at all = an unconstrained tier.
            days: row.days.size > 0
                ? WEEKDAYS.filter((d) => row.days.has(d)).join(',')
                : null,
            startTime: localTimeOfDayToStored(row.startLocal),
            endTime: localTimeOfDayToStored(row.endLocal),
        });
        const payload = [
            ...rows.map((row) => ({
                ...scopeFields(row),
                // Stated, not read back off a disabled input: the backend
                // admits this write only because every row is zero, so the zero
                // is the contract and belongs in the payload explicitly.
                price: priceLocked ? 0 : Number(row.price),
            })),
            // Class rates ride on their row's CURRENT scope — edit a row's
            // duration or window and its class rates move with it. A blank
            // field emits no row, which the backend archives: blank = "the
            // general rate applies", the same rule as the object page.
            //
            // Emitted from classPrices, NOT from offerableClasses: the class
            // list arrives async and is empty while loading, after a failed
            // fetch, and on free use cases — iterating it here would archive
            // every prefilled class rate in exactly those states, the silent
            // loss this dialog exists to prevent. A rate whose class is
            // meanwhile archived still goes out and the backend rejects the
            // save loudly (everyClassIsLive) — same behaviour as the object
            // page, and loud beats silent.
            ...rows.flatMap((row) =>
                Object.entries(classPrices[row.key] ?? {}).flatMap(([classId, value]) => {
                    const raw = value.trim();
                    if (raw === '') return [];
                    return [{
                        ...scopeFields(row),
                        price: Number(raw),
                        customerClassId: classId,
                    }];
                })),
            ...initial.passthrough,
        ];
        try {
            await updatePriceScales({
                priceCategoryId: group.priceCategory!.id,
                pricescales: payload,
            }).unwrap();
            onSaved();
            onClose();
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            setError(message ?? t('services.editor.save_failed'));
        }
    };

    const fieldClass =
        'w-full rounded-[5px] border border-foreground/15 bg-text-contrast px-3 py-2'
        + ' text-sm text-foreground';

    // Localized short weekday names, same Intl derivation the tables use.
    const locale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';
    const dayLabel: Record<WeekdayToken, string> = Object.fromEntries(
        WEEKDAYS.map((key, i) => [
            key,
            new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
                .format(new Date(Date.UTC(2024, 0, 1 + i))),
        ]),
    ) as Record<WeekdayToken, string>;

    return (
        <CustomModal
            open
            title={t('services.editor.title', {
                name: group.name ?? t('common.missing'),
            })}
            description={t('services.editor.description')}
            onClose={onClose}
            saveButton={(
                <CustomButton
                    title={t('common.save')}
                    onClick={handleSave}
                    loading={isLoading}
                    disabled={isLoading || anyInvalid || rows.length + initial.passthrough.length === 0}
                />
            )}
            closeButton={(
                <CustomButton title={t('common.cancel')} onClick={onClose} isFilled={false} />
            )}
        >
            <div className={'flex flex-col gap-4'}>
                {/* Prices are stored GROSS — see PricingSection's twin note. */}
                <PriceEntryNote />
                {sharedWithOthers && (
                    <p className={'text-xs text-foreground px-3 py-2 rounded-[5px] border border-foreground/20 bg-foreground/5'}>
                        {t('services.editor.shared_warning')}
                    </p>
                )}
                {/* Says what this editor still decides on a free use case —
                    which durations are bookable — so the disabled price field
                    reads as a rule rather than as a broken control. */}
                {priceLocked && (
                    <p className={'text-xs text-foreground px-3 py-2 rounded-[5px] border border-foreground/20 bg-foreground/5'}>
                        {t('services.editor.free_price_locked')}
                    </p>
                )}
                {rows.map((row) => {
                    const err = rowError(row);
                    const dupe = duplicateScopes.has(scopeOf(row));
                    return (
                        <div
                            key={row.key}
                            className={'rounded-[5px] border p-3 flex flex-col gap-3 '
                                + (err || dupe ? 'border-foreground/40' : 'border-foreground/10')}
                        >
                            <div className={'grid grid-cols-2 gap-3'}>
                                <label className={'block text-xs text-foreground/60'}>
                                    {t('services.editor.duration')}
                                    <input
                                        type={'number'}
                                        min={1}
                                        className={`${fieldClass} mt-1`}
                                        value={row.durationMinutes}
                                        onChange={(e) => updateRow(row.key, { durationMinutes: e.target.value })}
                                    />
                                </label>
                                {/* Disabled rather than hidden on a free use
                                    case: the operator should see that the row
                                    costs 0,00 and why, not wonder where the
                                    price went. Nothing to convert there. */}
                                {priceLocked ? (
                                    <label className={'block text-xs text-foreground/60'}>
                                        {t('services.editor.price')}
                                        <input
                                            type={'number'}
                                            min={0}
                                            step={'0.01'}
                                            className={`${fieldClass} mt-1 text-foreground/50`}
                                            value={FREE_PRICE}
                                            disabled
                                        />
                                    </label>
                                ) : (
                                    <PriceInput
                                        label={t('services.editor.price')}
                                        gross={row.price}
                                        onGrossChange={(price) => updateRow(row.key, { price })}
                                        rate={rate}
                                        inputClassName={fieldClass}
                                    />
                                )}
                            </div>

                            <div>
                                <div className={'flex items-center justify-between'}>
                                    <span className={'text-xs text-foreground/60'}>
                                        {t('services.editor.weekdays')}
                                    </span>
                                    <button
                                        type={'button'}
                                        className={'text-xs text-primary hover:underline'}
                                        onClick={() => updateRow(row.key, { days: new Set(WEEKDAYS) })}
                                    >
                                        {t('services.editor.all_days')}
                                    </button>
                                </div>
                                <div className={'mt-1 flex flex-wrap gap-1'}>
                                    {WEEKDAYS.map((day) => (
                                        <button
                                            key={day}
                                            type={'button'}
                                            onClick={() => toggleDay(row.key, day)}
                                            className={'rounded-full px-2.5 py-1 text-xs border '
                                                + (row.days.has(day)
                                                    ? 'bg-primary text-text-buttonContrast border-primary'
                                                    : 'border-foreground/15 text-foreground/70')}
                                        >
                                            {dayLabel[day]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={'grid grid-cols-2 gap-3'}>
                                <label className={'block text-xs text-foreground/60'}>
                                    {t('services.editor.from')}
                                    <input
                                        type={'time'}
                                        className={`${fieldClass} mt-1`}
                                        value={row.startLocal}
                                        onChange={(e) => updateRow(row.key, { startLocal: e.target.value })}
                                    />
                                </label>
                                <label className={'block text-xs text-foreground/60'}>
                                    {t('services.editor.until')}
                                    <input
                                        type={'time'}
                                        className={`${fieldClass} mt-1`}
                                        value={row.endLocal}
                                        onChange={(e) => updateRow(row.key, { endLocal: e.target.value })}
                                    />
                                </label>
                            </div>

                            {offerableClasses.length > 0 && (
                                <div className={'rounded-[5px] border border-foreground/10 p-3 flex flex-col gap-2'}>
                                    <span className={'text-xs font-medium text-foreground'}>
                                        {t('asset_detail.fields.class_rates')}
                                    </span>
                                    <div className={'grid grid-cols-2 gap-3'}>
                                        {offerableClasses.map((cls) => {
                                            const raw = classPrices[row.key]?.[cls.id] ?? '';
                                            return (
                                                <PriceInput
                                                    key={cls.id}
                                                    label={cls.name}
                                                    gross={raw}
                                                    onGrossChange={(price) => setClassPrice(row.key, cls.id, price)}
                                                    rate={rate}
                                                    inputClassName={fieldClass}
                                                />
                                            );
                                        })}
                                    </div>
                                    {offerableClasses.some((cls) =>
                                        classPriceError(classPrices[row.key]?.[cls.id] ?? '')) && (
                                        <p className={'text-xs text-foreground'}>
                                            {t('services.editor.error_price')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {err && <p className={'text-xs text-foreground'}>{err}</p>}
                            {dupe && (
                                <p className={'text-xs text-foreground'}>
                                    {t('services.editor.error_duplicate')}
                                </p>
                            )}

                            <div className={'flex justify-end'}>
                                <button
                                    type={'button'}
                                    className={'text-xs text-foreground/60 hover:underline'}
                                    onClick={() => removeRow(row.key)}
                                >
                                    {t('services.editor.remove_row')}
                                </button>
                            </div>
                        </div>
                    );
                })}

                <div>
                    <button
                        type={'button'}
                        className={'text-sm text-primary hover:underline font-medium'}
                        onClick={addRow}
                    >
                        {t('services.editor.add_row')}
                    </button>
                </div>

                {initial.passthrough.length > 0 && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('services.editor.passthrough_hint', { count: initial.passthrough.length })}
                    </p>
                )}

                {offerableClasses.length > 0 && (
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_detail.fields.class_rates_hint')}
                    </p>
                )}

                <p className={'text-xs text-foreground/60'}>
                    {t('services.editor.time_hint')}
                </p>

                {error && (
                    <div
                        role={'alert'}
                        className={'px-3 py-2 rounded-[5px] border border-foreground/20 bg-foreground/5 text-sm text-foreground'}
                    >
                        {error}
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default EditPriceScalesDialog;
